class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this._refreshRequestId = 0;
    this._postAddSyncTimer = null;

    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());

    // Delegation survives AJAX replacement of #CartDrawer-Overlay.
    this.addEventListener("click", (event) => {
      if (event.target.closest("#CartDrawer-Overlay")) this.close();
    });

    // Only the LIVE drawer in the document is allowed to control page scrolling.
    // A detached/stale drawer can still receive async callbacks after AJAX DOM updates;
    // it must never be able to lock <body>.
    this._drawerStateObserver = new MutationObserver(() => this.syncBodyScrollLock());
    this._drawerStateObserver.observe(this, { attributes: true, attributeFilter: ["class"] });

    this.setHeaderCartIconAccessibility();
    this.bindUpsellButton();
  }

  getLiveDrawer() {
    return document.querySelector("cart-drawer");
  }

  isLiveDrawer() {
    return this.isConnected && this.getLiveDrawer() === this;
  }

  syncBodyScrollLock() {
    const liveDrawer = this.getLiveDrawer();
    const shouldLock = Boolean(
      liveDrawer &&
      liveDrawer.isConnected &&
      liveDrawer.classList.contains("active")
    );
    document.body.classList.toggle("overflow-hidden", shouldLock);
  }

  unlockIfDrawerIsNotVisible() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const liveDrawer = this.getLiveDrawer();
        if (!liveDrawer || liveDrawer !== this || !this.isConnected) {
          this.syncBodyScrollLock();
          return;
        }

        const panel = this.querySelector(".drawer__inner") || this.querySelector("#CartDrawer");
        if (!panel) {
          this.classList.remove("active");
          this.syncBodyScrollLock();
          return;
        }

        const style = window.getComputedStyle(panel);
        const rect = panel.getBoundingClientRect();
        const visible =
          this.classList.contains("active") &&
          style.display !== "none" &&
          style.visibility !== "hidden" &&
          rect.width > 0 &&
          rect.height > 0;

        if (!visible) {
          this.classList.remove("active");
        }
        this.syncBodyScrollLock();
      });
    });
  }

  setHeaderCartIconAccessibility() {
    // Global delegation is installed even when the drawer element is temporarily
    // missing, so both the native Shopify icon and Emberly header icon can recover it.
    setupGlobalCartDrawerTriggers();
  }

  bindUpsellButton() {
    this.addEventListener("click", (event) => {
      const upsellButton = event.target.closest(".upsell-add-button");
      if (upsellButton) {
        event.preventDefault();

        const form = upsellButton.closest(".upsell-form");
        const variantId = form.querySelector('input[name="id"]').value;

        this.addItemToCart(variantId);
      }
    });
  }

  async addItemToCart(variantId) {
    const sections = this.getSectionsToRender().map((section) => section.id);

    const body = JSON.stringify({
      id: variantId,
      quantity: 1,
      sections: sections,
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(`${routes.cart_add_url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: `application/json`,
        },
        body,
      });

      if (response.status === 422) {
        const errorData = await response.json();
        // Handle error (e.g., show a notification)
        console.error("Error adding item to cart:", errorData.description);
        return;
      }

      const parsedState = await response.json();
      this.renderContents(parsedState);
    } catch (error) {
      console.error("Error adding item to cart:", error);
    }
  }

  invalidatePendingRefresh() {
    // Any new cart mutation invalidates an older section refresh so a stale
    // response can never overwrite the latest cart UI.
    this._refreshRequestId += 1;
    if (this._postAddSyncTimer) {
      clearTimeout(this._postAddSyncTimer);
      this._postAddSyncTimer = null;
    }
  }

  async syncAfterAdd(fallbackState = null) {
    // Product forms can hold an old cart-drawer reference after the cart became
    // empty and theme markup was refreshed. Always hand off to the live drawer.
    const liveDrawer = this.getLiveDrawer();
    if (liveDrawer && liveDrawer !== this && typeof liveDrawer.syncAfterAdd === "function") {
      return liveDrawer.syncAfterAdd(fallbackState);
    }

    if (!this.isLiveDrawer()) {
      this.syncBodyScrollLock();
      return false;
    }

    const requestId = ++this._refreshRequestId;
    const root = window.Shopify?.routes?.root || "/";
    const stamp = Date.now();

    try {
      // Use the simplest authoritative endpoints here. /cart.js tells us the real
      // state; ?section_id=cart-drawer returns the exact current drawer HTML.
      const [cartResponse, drawerResponse] = await Promise.all([
        fetch(`${root}cart.js?_=${stamp}`, {
          credentials: "same-origin",
          cache: "no-store",
          headers: { Accept: "application/json", "Cache-Control": "no-cache" },
        }),
        fetch(`${root}cart?section_id=cart-drawer&_=${stamp}`, {
          credentials: "same-origin",
          cache: "no-store",
          headers: { "Cache-Control": "no-cache" },
        }),
      ]);

      if (requestId !== this._refreshRequestId) return false;
      if (!cartResponse.ok || !drawerResponse.ok) throw new Error("Unable to refresh cart after add");

      const cartState = await cartResponse.json();
      const drawerHtml = await drawerResponse.text();
      if (requestId !== this._refreshRequestId) return false;

      const parsed = new DOMParser().parseFromString(drawerHtml, "text/html");
      const newCartDrawer = parsed.querySelector("#CartDrawer");
      const currentCartDrawer = this.querySelector("#CartDrawer");

      if (!newCartDrawer || !currentCartDrawer) {
        throw new Error("Cart drawer markup missing after add");
      }

      if (typeof updateCartDrawer === "function") {
        // Free-gift synchronization is handled separately. The primary add render
        // must be deterministic and must not recursively mutate the cart here.
        updateCartDrawer(currentCartDrawer, newCartDrawer, false);
      } else {
        currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
      }

      const itemCount = Number(cartState?.item_count || 0);
      this.classList.toggle("is-empty", itemCount === 0);
      if (typeof updateBubbleCount === "function") updateBubbleCount(itemCount);

      this.dispatchEvent(new CustomEvent("cart-drawer:render", {
        bubbles: true,
        detail: { cartData: cartState, item_count: itemCount, source: "sync-after-add" },
      }));

      if (itemCount > 0) {
        this.open(this.activeElement || undefined);
      } else {
        this.classList.remove("active");
        this.syncBodyScrollLock();
      }

      return true;
    } catch (error) {
      console.error("[Emberly cart] Post-add drawer sync failed:", error);

      // Fallback to Shopify's /cart/add section response if it is available.
      if (this.isLiveDrawer() && fallbackState?.sections) {
        this.renderContents({ ...fallbackState, __forceOpen: true });
        return true;
      }

      this.syncBodyScrollLock();
      return false;
    }
  }

  /**
   * Refresh cart drawer after AJAX cart operations
   * Can be called directly or will be triggered by 'cart:ajax-update' event
   * @param {Object} sectionsData - Optional sections data from cart response
   */
  async refreshCartDrawer(sectionsData = null, options = {}) {
    const refreshRequestId = ++this._refreshRequestId;
    const forceOpen = options?.forceOpen === true;

    try {
      // Shopify section-rendering responses do not reliably include item_count.
      // Always get the real cart state so an ordinary quantity update is never
      // mistaken for an empty cart.
      const cartResponse = await fetch(`${window.Shopify?.routes?.root || "/"}cart.js?_=${Date.now()}`, {
        credentials: "same-origin",
        cache: "no-store",
        headers: { Accept: "application/json", "Cache-Control": "no-cache" },
      });
      const cartState = cartResponse.ok ? await cartResponse.json() : null;
      if (refreshRequestId !== this._refreshRequestId) return false;

      if (sectionsData && sectionsData.sections) {
        this.renderContents({
          ...sectionsData,
          id: sectionsData.id || null,
          item_count: cartState?.item_count ?? sectionsData.item_count ?? 0,
          __fromRefresh: true,
          __forceOpen: forceOpen,
        });
        return true;
      }

      const sections = this.getSectionsToRender().map((section) => section.id);
      const cartUrl = window.routes?.cart_url || window.Shopify?.routes?.root + "cart" || "/cart";
      const joiner = cartUrl.includes("?") ? "&" : "?";
      const url = `${cartUrl}${joiner}sections=${sections.join(",")}&_=${Date.now()}`;
      const response = await fetch(url, {
        credentials: "same-origin",
        cache: "no-store",
        headers: { "Cache-Control": "no-cache" },
      });
      if (!response.ok) return false;

      const parsedState = await response.json();
      if (refreshRequestId !== this._refreshRequestId) return false;
      let renderedSections = parsedState.sections || null;

      if (!renderedSections) {
        renderedSections = {};
        sections.forEach((sectionId) => {
          if (parsedState[sectionId]) renderedSections[sectionId] = parsedState[sectionId];
        });
      }

      if (!renderedSections || Object.keys(renderedSections).length === 0) return false;

      this.renderContents({
        sections: renderedSections,
        id: parsedState.id || null,
        item_count: cartState?.item_count ?? 0,
        __fromRefresh: true,
        __forceOpen: forceOpen,
      });
      return true;
    } catch (error) {
      console.error("Error refreshing cart drawer:", error);
      return false;
    }
  }

  schedulePostAddReconcile() {
    if (this._postAddSyncTimer) clearTimeout(this._postAddSyncTimer);

    // Debounce repeated successful adds and reconcile once with the latest
    // server cart. refreshCartDrawer itself is request-versioned, so an older
    // response can never overwrite a newer add.
    this._postAddSyncTimer = setTimeout(() => {
      this._postAddSyncTimer = null;
      this.refreshCartDrawer();
    }, 250);
  }

  startCountdownTimer() {
    const timerElement = document.getElementById("cart-countdown-timer");
    if (!timerElement) return;

    // Reset timer to 10 minutes
    let minutes = 10;
    let seconds = 0;

    // Clear any existing timer
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    // Update the timer display
    const updateTimer = () => {
      timerElement.textContent = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    };

    // Initial display
    updateTimer();

    // Start the countdown
    this.countdownInterval = setInterval(() => {
      if (seconds === 0) {
        if (minutes === 0) {
          clearInterval(this.countdownInterval);
          return;
        }
        minutes--;
        seconds = 59;
      } else {
        seconds--;
      }

      updateTimer();
    }, 1000);
  }

  open(triggeredBy) {
    // If an async callback reaches an old drawer instance, hand the open request
    // to the cart drawer that is currently mounted in the document.
    const liveDrawer = document.querySelector("cart-drawer");
    if (liveDrawer && liveDrawer !== this && typeof liveDrawer.open === "function") {
      return liveDrawer.open(triggeredBy);
    }
    if (!this.isConnected) return;

    if (triggeredBy) this.setActiveElement(triggeredBy);

    const alreadyOpen = this.classList.contains("active");
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    // Do not use computed visibility to decide whether opening succeeded. During
    // the CSS transition the drawer can still report visibility:hidden / zero-ish
    // geometry, which previously caused .active to be removed immediately.
    this.classList.add("animate");
    this.classList.add("active");
    document.body.classList.add("overflow-hidden");

    if (!alreadyOpen) {
      this.addEventListener(
        "transitionend",
        () => {
          const containerToTrapFocusOn = this.classList.contains("is-empty")
            ? this.querySelector(".drawer__inner-empty")
            : this.querySelector("#CartDrawer") || this;
          const focusElement = this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
          if (containerToTrapFocusOn && focusElement && typeof trapFocus === "function") {
            trapFocus(containerToTrapFocusOn, focusElement);
          }
        },
        { once: true },
      );
    }

    if (typeof $ !== "undefined" && typeof $.fn.slick !== "undefined") {
      const $slider = $(".gbfrequently-bought-with-main-whole-slider");
      if ($slider.length > 0 && !$slider.hasClass("slick-initialized")) {
        $slider.slick({
          infinite: true,
          autoplaySpeed: 1000,
          autoplay: false,
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
        });
        $slider.css("opacity", "1");
      }
    }

    this.startCountdownTimer();

    this.dispatchEvent(new CustomEvent("cart-drawer:open", {
      bubbles: true,
      detail: { triggeredBy },
    }));
  }

  close() {
    const cartCustomizationMode = this.classList.contains("customization-mode");
    if (cartCustomizationMode) {
      alert("Cart Customization Mode is active. Disable it in Theme Settings > Cart Drawer to allow closing.");
      return;
    }

    // Remove visual state first; always release scroll even if focus cleanup fails.
    this.classList.remove("active");
    try {
      if (typeof removeTrapFocus === "function") {
        removeTrapFocus(this.activeElement || null);
      }
    } catch (error) {
      console.warn("Cart drawer focus cleanup failed:", error);
    } finally {
      this.syncBodyScrollLock();
    }

    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    const wasOpen = this.classList.contains("active");
    const forceOpen = parsedState.__forceOpen === true;
    const isFreshAdd =
      !parsedState.__fromRefresh &&
      (!!parsedState.id ||
        (Array.isArray(parsedState.items) && parsedState.items.length > 0) ||
        !!parsedState.sections);
    let resolvedItemCount = Number.isFinite(Number(parsedState.item_count))
      ? Number(parsedState.item_count)
      : null;

    this.productId = parsedState.id || this.productId || null;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      if (section.id === "cart-drawer" || section.selector === "#CartDrawer") {
        const sectionHtml = parsedState.sections?.[section.id];
        const newCartDrawer = this.getSectionDOM(sectionHtml, section.selector);
        if (!newCartDrawer) return;

        // /cart/add section responses do not include item_count at the top level.
        // Resolve it directly from the freshly rendered cart drawer so an
        // empty -> non-empty add is deterministic.
        if (resolvedItemCount === null) {
          const countText = newCartDrawer.querySelector(".gb-cart-total-item")?.textContent?.trim();
          if (countText !== undefined && countText !== null && countText !== "") {
            const parsedCount = Number.parseInt(countText, 10);
            if (Number.isFinite(parsedCount)) resolvedItemCount = parsedCount;
          }
        }

        if (typeof updateCartDrawer === "function") {
          updateCartDrawer(sectionElement, newCartDrawer);
        }
      } else if (section.id === "cart-icon-bubble") {
        const html = parsedState.sections?.[section.id];
        if (!html) return;

        const iconType = sectionElement.getAttribute("data-cart-icon-type");
        sectionElement.innerHTML = this.getSectionInnerHTML(html, section.selector);
        if (iconType) sectionElement.setAttribute("data-cart-icon-type", iconType);
      } else {
        const html = parsedState.sections?.[section.id];
        if (html) sectionElement.innerHTML = this.getSectionInnerHTML(html, section.selector);
      }
    });

    // Fallbacks if a customized drawer header omitted the count span.
    if (resolvedItemCount === null) {
      const bubbleCount = document.querySelector("#cart-icon-bubble .cart-count-bubble span")?.textContent;
      const parsedBubbleCount = Number.parseInt(bubbleCount || "", 10);
      if (Number.isFinite(parsedBubbleCount)) resolvedItemCount = parsedBubbleCount;
    }
    if (resolvedItemCount === null) {
      resolvedItemCount = this.querySelector(".cart-item") ? 1 : 0;
    }

    this.classList.toggle("is-empty", resolvedItemCount === 0);
    if (typeof updateBubbleCount === "function") updateBubbleCount(resolvedItemCount);

    this.dispatchEvent(new CustomEvent("cart-drawer:render", {
      bubbles: true,
      detail: { parsedState, item_count: resolvedItemCount }
    }));

    if (resolvedItemCount === 0) {
      // Empty cart must never retain a page scroll lock.
      this.classList.remove("active");
      this.syncBodyScrollLock();
    } else if (forceOpen || isFreshAdd) {
      // Successful Add to Cart must recover from the empty-cart template for
      // both single-item and multi-item /cart/add responses.
      this.open(this.activeElement || undefined);
      if (!forceOpen) this.schedulePostAddReconcile();
    } else if (!wasOpen) {
      // A background refresh must not create a new scroll lock.
      this.classList.remove("active");
      this.syncBodyScrollLock();
    }

    if (typeof $ !== "undefined" && typeof $.fn.slick !== "undefined") {
      const $slider = $(".gbfrequently-bought-with-main-whole-slider");
      if ($slider.length > 0 && !$slider.hasClass("slick-initialized")) {
        $slider.slick({
          infinite: true,
          autoplaySpeed: 1000,
          autoplay: false,
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
        });
        $slider.css("opacity", "1");
      }
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    if (!html) return "";
    const element = new DOMParser().parseFromString(html, "text/html").querySelector(selector);
    return element ? element.innerHTML : "";
  }

  getSectionsToRender() {
    setTimeout(function () {
      // Check if jQuery and slick are loaded before initializing
      if (typeof $ !== "undefined" && typeof $.fn.slick !== "undefined") {
        var $slider = $(".gbfrequently-bought-with-main-whole-slider");
        if ($slider.length > 0 && !$slider.hasClass("slick-initialized")) {
          $slider.slick({
            infinite: true,
            autoplaySpeed: 1000,
            autoplay: false,
            slidesToShow: 1,
            slidesToScroll: 1,
            arrows: false,
            dots: true,
            // adaptiveHeight: true
            //prevArrow: $('.our-customer-slider-prev'),
            //nextArrow: $('.our-customer-slider-next'),
          });
          $slider.css("opacity", "1");
        }
      }
    }, 500);

    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    if (!html) return null;
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

let emberlyCartDrawerMountPromise = null;

async function ensureEmberlyCartDrawer() {
  const existingDrawer = document.querySelector("cart-drawer");
  if (existingDrawer) return existingDrawer;

  if (emberlyCartDrawerMountPromise) return emberlyCartDrawerMountPromise;

  emberlyCartDrawerMountPromise = (async () => {
    const root = window.Shopify?.routes?.root || "/";
    const stamp = Date.now();

    try {
      const response = await fetch(`${root}cart?section_id=cart-drawer&_=${stamp}`, {
        credentials: "same-origin",
        cache: "no-store",
        headers: { "Cache-Control": "no-cache" },
      });

      if (!response.ok) {
        throw new Error(`Unable to load cart drawer section (${response.status})`);
      }

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, "text/html");
      const freshSection = parsed.querySelector("#shopify-section-cart-drawer");
      const freshDrawer = parsed.querySelector("cart-drawer");

      if (!freshDrawer) {
        throw new Error("Shopify cart-drawer section did not contain <cart-drawer>");
      }

      // Remove malformed/orphaned drawer markup left behind by an interrupted AJAX
      // update. The real drawer must always be wrapped by <cart-drawer>.
      const orphanCartDrawer = document.getElementById("CartDrawer");
      if (orphanCartDrawer && !orphanCartDrawer.closest("cart-drawer")) {
        orphanCartDrawer.remove();
      }

      const existingSection = document.querySelector("#shopify-section-cart-drawer");
      if (existingSection && freshSection) {
        existingSection.replaceWith(document.importNode(freshSection, true));
      } else if (existingSection) {
        existingSection.replaceChildren(document.importNode(freshDrawer, true));
      } else if (freshSection) {
        document.body.appendChild(document.importNode(freshSection, true));
      } else {
        document.body.appendChild(document.importNode(freshDrawer, true));
      }

      if (customElements.get("cart-drawer")) {
        await customElements.whenDefined("cart-drawer");
      }

      // Allow the inserted custom element to connect before returning it.
      await new Promise((resolve) => requestAnimationFrame(resolve));

      const mountedDrawer = document.querySelector("cart-drawer");
      if (!mountedDrawer) {
        throw new Error("Cart drawer could not be mounted into the product page");
      }

      return mountedDrawer;
    } catch (error) {
      console.error("[Emberly cart] Unable to restore cart drawer:", error);
      document.body.classList.remove("overflow-hidden");
      return null;
    } finally {
      emberlyCartDrawerMountPromise = null;
    }
  })();

  return emberlyCartDrawerMountPromise;
}

window.ensureEmberlyCartDrawer = ensureEmberlyCartDrawer;

function setupGlobalCartDrawerTriggers() {
  if (window.__emberlyCartDrawerIconDelegationBound) return;
  window.__emberlyCartDrawerIconDelegationBound = true;

  document.addEventListener("click", async (event) => {
    const cartTrigger = event.target.closest?.("#cart-icon-bubble, [data-eh-cart]");
    if (!cartTrigger) return;

    // Prevent the normal /cart navigation immediately. If the drawer is missing,
    // restore it in-place and keep the customer on the product page.
    event.preventDefault();

    const drawer = await ensureEmberlyCartDrawer();
    if (drawer && typeof drawer.open === "function") {
      drawer.open(cartTrigger);
    }
  });

  document.addEventListener("keydown", async (event) => {
    if (event.code !== "Space" && event.code !== "Enter") return;
    const cartTrigger = event.target.closest?.("#cart-icon-bubble, [data-eh-cart]");
    if (!cartTrigger) return;

    event.preventDefault();
    const drawer = await ensureEmberlyCartDrawer();
    if (drawer && typeof drawer.open === "function") {
      drawer.open(cartTrigger);
    }
  });
}

if (!customElements.get("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawer);
}

// Install header/cart-icon recovery even on pages where the drawer markup is
// temporarily absent when this script first runs.
setupGlobalCartDrawerTriggers();

function setupAjaxCartHandler() {
  if (window.__emberlyAjaxCartHandlerBound) return;
  window.__emberlyAjaxCartHandlerBound = true;

  document.addEventListener("cart:ajax-update", async function (event) {
    const sectionsData = event.detail?.sectionsData || null;
    const drawer = await ensureEmberlyCartDrawer();
    if (drawer && typeof drawer.refreshCartDrawer === "function") {
      await drawer.refreshCartDrawer(sectionsData);
    }
  });

  window.refreshCartDrawer = async function (sectionsData = null) {
    const drawer = await ensureEmberlyCartDrawer();
    if (drawer && typeof drawer.refreshCartDrawer === "function") {
      await drawer.refreshCartDrawer(sectionsData);
    }
  };
}

if (document.readyState === 'loading') {
  document.addEventListener("DOMContentLoaded", setupAjaxCartHandler);
} else {
  setupAjaxCartHandler();
}

// Wait for CartItems to be defined before extending it
if (typeof CartItems !== "undefined") {
  // CartItems is already loaded
  defineCartDrawerItems();
} else {
  // Wait for cart.js to load and define CartItems
  const checkCartItems = setInterval(() => {
    if (typeof CartItems !== "undefined") {
      clearInterval(checkCartItems);
      defineCartDrawerItems();
    }
  }, 50);
}

function defineCartDrawerItems() {
  if (customElements.get("cart-drawer-items")) {
    return; // Already defined
  }

  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      setTimeout(function () {
        // Check if jQuery and slick are loaded before initializing
        if (typeof $ !== "undefined" && typeof $.fn.slick !== "undefined") {
          const $slider = $(".gbfrequently-bought-with-main-whole-slider");
          if ($slider.length > 0) {
            $slider.slick({
              infinite: true,
              autoplaySpeed: 1000,
              autoplay: false,
              slidesToShow: 1,
              slidesToScroll: 1,
              arrows: false,
              dots: true,
            });
            $slider.css("opacity", "1");
          }
        }
      }, 500);

      return [
        {
          id: "CartDrawer",
          section: "cart-drawer",
          selector: ".drawer__inner",
        },
        {
          id: "cart-icon-bubble",
          section: "cart-icon-bubble",
          selector: ".shopify-section",
        },
      ];
    }
  }

  customElements.define("cart-drawer-items", CartDrawerItems);
}

(() => {
  if (window.emberlyCheckoutLoadingBound) return;
  window.emberlyCheckoutLoadingBound = true;

  let activeButton = null;
  let resetTimer;

  function resetCheckoutLoader() {
    clearTimeout(resetTimer);

    if (!activeButton) return;

    activeButton.classList.remove('loading');
    activeButton.removeAttribute('aria-busy');
    activeButton.querySelector('.loading__spinner')
      ?.classList.add('hidden');

    activeButton = null;
  }

  // Prevent repeat clicks while checkout is opening.
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('#CartDrawer-Checkout');

    if (button && activeButton) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  // Delegation also works after AJAX cart updates.
  document.addEventListener('submit', (event) => {
    const button = event.submitter;

    if (
      event.target.id !== 'CartDrawer-Form' ||
      button?.id !== 'CartDrawer-Checkout' ||
      button.disabled ||
      event.defaultPrevented
    ) return;

    if (activeButton) {
      event.preventDefault();
      return;
    }

    activeButton = button;
    button.classList.add('loading');
    button.setAttribute('aria-busy', 'true');
    button.querySelector('.loading__spinner')
      ?.classList.remove('hidden');

    // Preserve Shopify's normal checkout submission.
    queueMicrotask(() => {
      if (event.defaultPrevented) resetCheckoutLoader();
    });

    // Allow another attempt if navigation fails.
    resetTimer = setTimeout(resetCheckoutLoader, 20000);
  });

  window.addEventListener('pageshow', resetCheckoutLoader);
})();